import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import client from "../api/client";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { QRCodeCanvas } from "qrcode.react";

const fmt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

export default function InvoicePrintPage() {
  const { id } = useParams();
  const invoiceRef = useRef(null);
  const [invoice, setInvoice] = useState(null);


  useEffect(() => {
    load();
  }, [id]);

  const load = async () => {
    const res = await client.get(`/sales/invoices/${id}`);
    setInvoice(res.data);
  };

  const downloadPDF = async () => {
    const element = invoiceRef.current;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgWidth = pageWidth - 20;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
    const header = invoice?.header || {};
    pdf.save(`Invoice-${header.InvoiceNo ?? header.invoiceNo ?? id}.pdf`);
  };

  if (!invoice) return <div className="p-4">Loading invoice...</div>;

  const h = invoice.header;
  const details = invoice.details || [];
  const company = {
    name: h.CompanyName ?? h.companyName ?? "Your Company Name",
    gstNo: h.CompanyGSTIN ?? h.companyGSTIN ?? h.companyGstin ?? "-",
    phone: h.CompanyPhone ?? h.companyPhone ?? "-",
    address: h.CompanyAddress ?? h.companyAddress ?? "-",
    logo: h.CompanyLogoUrl ?? h.companyLogoUrl ?? "",
    bank: "Bank details can be configured in invoice template/settings",
  };

  const qrText = `
Invoice No: ${h.InvoiceNo ?? h.invoiceNo}
Date: ${h.InvoiceDate ?? h.invoiceDate}
Customer: ${h.CustomerName ?? h.customerName}
GSTIN: ${h.GSTNo ?? h.gstNo ?? "-"}
Amount: ${fmt(h.GrandTotal ?? h.grandTotal)}
Company GSTIN: ${company.gstNo}
`;

  return (
    <div className="container my-4">
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }

            #invoice-print, #invoice-print * {
              visibility: visible;
            }

            #invoice-print {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }

            .no-print {
              display: none !important;
            }
          }
        `}
      </style>

      <div className="mb-3 d-flex gap-2 no-print">
        <button
          className="btn btn-primary btn-sm"
          onClick={() => window.print()}
        >
          Print
        </button>

        <button className="btn btn-success btn-sm" onClick={downloadPDF}>
          Download PDF
        </button>
      </div>

      <div
        ref={invoiceRef}
        id="invoice-print"
        className="bg-white border p-4"
        style={{
          maxWidth: "900px",
          margin: "0 auto",
          fontSize: "13px",
        }}
      >
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3">
          <div className="d-flex align-items-center gap-3" style={{ minHeight: 76 }}>
            {company.logo ? (
              <img
                src={company.logo}
                alt="Company Logo"
                style={{
                  width: 70,
                  height: 70,
                  objectFit: "contain",
                  flex: "0 0 70px",
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.parentElement.style.gap = "0px";
                }}
              />
            ) : null}

            <div style={{ marginLeft: company.logo ? 0 : 0 }}>
              <h4 className="mb-1 fw-bold">{company.name}</h4>
              <div>{company.address}</div>
              <div>
                GSTIN: {company.gstNo} | Phone: {company.phone}
              </div>
            </div>
          </div>

          <div className="text-end">
            <h3 className="fw-bold mb-1">TAX INVOICE</h3>
            <QRCodeCanvas value={qrText} size={80} />
          </div>
        </div>

        {/* Customer + Invoice Info */}
        <div className="row mb-3">
          <div className="col-6">
            <h6 className="fw-bold">Bill To:</h6>
            <div>{h.CustomerName ?? h.customerName}</div>
            <div>{h.AddressLine ?? h.addressLine}</div>
            <div>GSTIN: {h.GSTNo ?? h.gstNo ?? "-"}</div>
          </div>

          <div className="col-6 text-end">
            <div>
              <strong>Invoice No:</strong> {h.InvoiceNo ?? h.invoiceNo}
            </div>
            <div>
              <strong>Date:</strong> {h.InvoiceDate ?? h.invoiceDate}
            </div>
          </div>
        </div>

        {/* Items */}
        <table className="table table-bordered table-sm align-middle">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Item</th>
              <th>HSN</th>
              <th className="text-end">Qty</th>
              <th className="text-end">Rate</th>
              <th className="text-end">GST %</th>
              <th className="text-end">CGST</th>
              <th className="text-end">SGST</th>
              <th className="text-end">IGST</th>
              <th className="text-end">Total</th>
            </tr>
          </thead>

          <tbody>
            {details.map((d, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{d.ItemName ?? d.itemName}</td>
                <td>{d.HSNCode ?? d.hsnCode}</td>
                <td className="text-end">{fmt(d.Qty ?? d.qty)}</td>
                <td className="text-end">{fmt(d.Rate ?? d.rate)}</td>
                <td className="text-end">
                  {fmt(d.GSTPercent ?? d.gstPercent)}
                </td>
                <td className="text-end">{fmt(d.CGST ?? d.cgst)}</td>
                <td className="text-end">{fmt(d.SGST ?? d.sgst)}</td>
                <td className="text-end">{fmt(d.IGST ?? d.igst)}</td>
                <td className="text-end">{fmt(d.LineTotal ?? d.lineTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="row justify-content-end">
          <div className="col-5">
            <table className="table table-bordered table-sm">
              <tbody>
                <tr>
                  <th>Sub Total</th>
                  <td className="text-end">{fmt(h.SubTotal ?? h.subTotal)}</td>
                </tr>
                <tr>
                  <th>Total GST</th>
                  <td className="text-end">
                    {fmt(h.TaxAmount ?? h.taxAmount)}
                  </td>
                </tr>
                <tr>
                  <th>Discount</th>
                  <td className="text-end">
                    {fmt(h.DiscountAmount ?? h.discountAmount)}
                  </td>
                </tr>
                <tr className="fw-bold">
                  <th>Grand Total</th>
                  <td className="text-end">
                    {fmt(h.GrandTotal ?? h.grandTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Bank + Signature */}
        <div className="row mt-4">
          <div className="col-7">
            <strong>Bank Details:</strong>
            <div>{company.bank}</div>
            <div className="mt-3">
              <strong>Declaration:</strong>
              <br />
              We declare that this invoice shows the actual price of the goods
              described.
            </div>
          </div>

          <div className="col-5 text-end">
            <div className="mb-5">
              For <strong>{company.name}</strong>
            </div>

            <div
              style={{
                borderTop: "1px solid #000",
                display: "inline-block",
                paddingTop: 6,
                minWidth: 180,
              }}
            >
              Authorised Signature
            </div>
          </div>
        </div>

        <div className="text-center border-top pt-2 mt-4">
          <small>Thank you for your business.</small>
        </div>
      </div>
    </div>
  );
}
